import React, { useState } from 'react';

import { Alert, Box, Chip, Paper, alpha } from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import type { PointsPurchaseOfferPreview } from '~/types/loyaltyContract';

import PayForPointsToolbar from './PayForPointsToolbar';

const DEFAULT_PAGE_SIZE = 25;

function getPreviewStatusChipColor(status: string): 'success' | 'default' {
  return status === 'ACTIVE' ? 'success' : 'default';
}

interface OffersPreviewTableProps {
  offers: Array<PointsPurchaseOfferPreview>;
  isLoading: boolean;
  error: Error | null;
}

const columns: Array<GridColDef<PointsPurchaseOfferPreview>> = [
  { field: 'id', headerName: 'ID', width: 120 },
  { field: 'title', headerName: 'Title', flex: 1, minWidth: 180 },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    display: 'flex',
    renderCell: ({ value }: GridRenderCellParams<PointsPurchaseOfferPreview>) => (
      <Chip label={value} size="small" color={getPreviewStatusChipColor(value)} />
    ),
  },
  {
    field: 'basePoints',
    headerName: 'Base Points',
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => value?.toLocaleString(),
  },
  {
    field: 'bonusPointsPercent',
    headerName: 'Bonus %',
    width: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => (value != null ? `${value}%` : '-'),
  },
  {
    field: 'bonusPoints',
    headerName: 'Bonus Points',
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => value?.toLocaleString() ?? '-',
  },
  {
    field: 'totalPoints',
    headerName: 'Total Points',
    width: 130,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => value?.toLocaleString(),
  },
  {
    field: 'discountPercent',
    headerName: 'Discount %',
    width: 110,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => (value != null ? `${value}%` : '-'),
  },
  {
    field: 'basePrice',
    headerName: 'Base Price',
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => (value != null ? value.toFixed(2) : '-'),
  },
  {
    field: 'publicPrice',
    headerName: 'Public Price',
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value: number) => (value != null ? value.toFixed(2) : '-'),
  },
  { field: 'currency', headerName: 'Currency', width: 80 },
];

export default function OffersPreviewTable({ offers, isLoading, error }: OffersPreviewTableProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  return (
    <Box>
      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading preview: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      <Paper elevation={0} sx={{ height: 650 }}>
        <DataGridPro
          rows={offers}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{ toolbar: PayForPointsToolbar }}
          sx={{
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            },
          }}
        />
      </Paper>
    </Box>
  );
}
